import React from 'react';

import {Group, Item} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import {Loading, Types} from '@pb/kit-component-loading';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './textarea.component.module.styl';

import type {InputProps} from '@pb/kit-component-input-v2';
import type {ComponentProps, ComponentType, ReactNode, Ref} from 'react';


const classNames = withCssModule(styles);

const getInputProps = <T extends ComponentProps<'textarea'>>(props: TextareaProps<T>) => {
    // @eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {textareaComponent, adornment, busy, resizable,
        ...inputProps} = props;
    return inputProps;
};

const TYPE_CLASSES = {
    SUCCESS: 'textarea__adornment_success',
    ERROR: 'textarea__adornment_error',
    DEFAULT: '',
};

export type TextareaProps<T extends ComponentProps<'textarea'> = ComponentProps<'textarea'>> = T & {
    /**
     * Visual validation state controlling the border and adornment color:
     * - `"DEFAULT"` — neutral, no color indicator (default).
     * - `"SUCCESS"` — green, for confirmed valid input.
     * - `"ERROR"` — red; also sets `aria-invalid="true"` on the textarea.
     */
    color?: 'SUCCESS' | 'ERROR' | 'DEFAULT',
    /**
     * When `true`, sets the textarea to `readOnly` and overlays a loading
     * spinner in the adornment area. Use while async data is being submitted
     * or fetched.
     */
    busy?: boolean,
    /**
     * When `true` (default), the user can resize the textarea by dragging the
     * resize handle. Set to `false` to lock the size.
     */
    resizable?: boolean,
    /**
     * Node rendered in the bottom-right adornment area below the textarea.
     * Commonly used for character count badges or action icons. When `busy` is
     * also `true`, a loading spinner is prepended to this node.
     */
    adornment?: ReactNode,
    /**
     * Ref to the outer `<div>` wrapper rendered when `adornment` is present.
     * Has no effect when there is no adornment.
     */
    rootRef?: Ref<HTMLDivElement>,
    /**
     * Native `<div>` props spread onto the root wrapper rendered when
     * `adornment` is present. Has no effect when there is no adornment.
     */
    rootProps?: ComponentProps<'div'>,
    /**
     * Replacement for the native `<textarea>` element. Pass a custom component
     * accepting `ComponentProps<"textarea">` to integrate with masked-input or
     * rich-text libraries.
     */
    textareaComponent?: ComponentType<T>,
};

/**
 * Multiline text field built on top of `Input V2` and a native `<textarea>`.
 *
 * Use `color` for validation states, `busy` to indicate an async operation,
 * `adornment` for decorative or interactive elements below the field, and
 * `textareaComponent` to swap in a custom implementation.
 *
 * ## Accessibility
 * Accepts all native `<textarea>` attributes. Pair with `aria-label` /
 * `aria-labelledby` and `aria-describedby` per the HTML forms spec.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-textarea--documentation}
 */
export function Textarea<T extends ComponentProps<'textarea'> = ComponentProps<'textarea'>>(props: TextareaProps<T>) {
    const {
        color,
        textareaComponent: TextareaComponent = 'textarea',
        resizable = true,
        ref,
    } = props;

    const inputProps = getInputProps(props);

    const {adornment, readOnly} = renderBusy(props);

    const input = (
        <Input
            {...inputProps as InputProps & T}
            readOnly={readOnly}
            color={color}
            inputComponent={TextareaComponent as InputProps['inputComponent']}
            ref={ref as Ref<HTMLInputElement>}
            className={classNames(inputProps.className, 'textarea__component', {
                'textarea__component_not-resizable': !resizable,
                'textarea__component_disabled': inputProps.disabled,
            })}
        />
    );

    return adornment ? (
        <TextareaWithAdornment
            props={props}
            input={input}
            adornment={adornment}
        />
    ) : input;
}

Textarea.displayName = 'Textarea';

function renderBusy<T extends ComponentProps<'textarea'>>(props: TextareaProps<T>) {
    let {adornment, readOnly} = props;
    if (props.busy) {
        readOnly = true;
        const loader = <Loading type={Types.FIELD_ICON}/>;
        adornment = adornment ? [
            <Item key={1}>{loader}</Item>,
            <Item key={2}>{adornment}</Item>,
        ] : loader;
    }
    return {adornment, readOnly};
}

function TextareaWithAdornment<T extends ComponentProps<'textarea'>>({
    props, input, adornment,
}: {
    props: TextareaProps<T>,
    adornment: ReactNode,
    input: ReactNode,
}) {

    return (
        <div
            ref={props.rootRef}
            {...props.rootProps}
            className={classNames(props.rootProps?.className, 'textarea__root')}>
            {input}
            <Group
                gap="s"
                alignItems="center"
                className={classNames('textarea__adornment', TYPE_CLASSES[props.color ?? 'DEFAULT'], {
                    textarea__adornment_disabled: props.disabled,
                })}>
                {adornment}
            </Group>
        </div>
    );
}
